import { BaseTexture, Rectangle, Texture } from "pixi.js"

/** One square of a SlotAtlas page, and the texture showing it. */
export interface AtlasSlot {
    texture: Texture
    x: number
    y: number
    page: number
    index: number
}

/**
 * Same-sized pieces painted on the CPU and repainted now and then (the raised ground's chunks) on shared canvas pages:
 * a page holds dozens, so a room's chunks draw in a batch or two instead of one texture each. A piece keeps its slot
 * while it is repainted and gives it back when it goes; a gap of transparent pixels keeps nearest sampling at an edge
 * from picking up a neighbour.
 */
export class SlotAtlas {
    private pages: { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D; base: BaseTexture; free: number[]; used: number }[] = []
    private readonly perRow: number

    constructor(readonly size: number, private readonly pageSize = 1024, private readonly gap = 1) {
        this.perRow = Math.max(1, Math.floor((pageSize + gap) / (size + gap)))
    }

    /** A free slot (on a new page when every page is full); undefined without a 2D canvas. */
    claim(): AtlasSlot | undefined {
        let page = this.pages.findIndex(p => p.free.length > 0 || p.used < this.perRow * this.perRow)
        if (page < 0) {
            const canvas = document.createElement("canvas")
            canvas.width = this.pageSize
            canvas.height = this.pageSize
            // In memory, not on the GPU: a painter may read its pixels back (willReadFrequently), and a big canvas on the
            // GPU makes every such read a readback.
            const context = canvas.getContext("2d", { willReadFrequently: true } as CanvasRenderingContext2DSettings) as CanvasRenderingContext2D | null
            if (!context) return undefined
            this.pages.push({ canvas, context, base: BaseTexture.from(canvas), free: [], used: 0 })
            page = this.pages.length - 1
        }
        const p = this.pages[page]
        const index = p.free.length ? p.free.pop()! : p.used++
        const x = (index % this.perRow) * (this.size + this.gap), y = Math.floor(index / this.perRow) * (this.size + this.gap)
        return { texture: new Texture(p.base, new Rectangle(x, y, this.size, this.size)), x, y, page, index }
    }

    /** Paints a slot's pixels (size x size RGBA), shown from the next frame. */
    paint(slot: AtlasSlot, rgba: Uint8ClampedArray): void {
        const p = this.pages[slot.page]
        if (!p) return
        p.context.putImageData(new ImageData(rgba, this.size, this.size), slot.x, slot.y)
        p.base.update()
    }

    /** The page a slot is on, to paint part of it (at the slot's x, y) or read it back; call touch() after painting. */
    context(slot: AtlasSlot): CanvasRenderingContext2D | undefined {
        return this.pages[slot.page]?.context
    }

    /** Shows what was painted into a slot through context(), from the next frame. */
    touch(slot: AtlasSlot): void {
        this.pages[slot.page]?.base.update()
    }

    /** Gives a slot back (cleared, so a later piece starts from nothing). */
    release(slot: AtlasSlot): void {
        const p = this.pages[slot.page]
        if (!p) return
        p.context.clearRect(slot.x, slot.y, this.size, this.size)
        p.base.update()
        p.free.push(slot.index)
    }

    /** Every slot given back and the pages freed (a frame later: the sprites using them go first). */
    reset(): void {
        const old = this.pages
        this.pages = []
        setTimeout(() => old.forEach(p => p.base.destroy()), 0)
    }

    get pageCount(): number {
        return this.pages.length
    }
}
